/**
 * Print buttons for documents an order already has (picking slip, the Verify-time box documents,
 * the shipped order's documents). With RxPrint on for the vendor, a Print button sends the document
 * to the printer mapped for it in Settings → RxPrint — at the user's shipping lane when they have
 * one — without regenerating it. Server side: libs/services/src/printnode/print-documents.ts.
 * Without RxPrint the same buttons download the file and read "Download…". With it they read
 * "Re-print…": a document mapped to a printer already printed by itself when it was generated.
 *
 * Whether RxPrint is on comes from a `company(id:){ id printNodeEnabled }` read made when the
 * screen opens — useRxPrintEnabled, or the screen's own one-shot query — never the root store
 * (fetched once per app load), and never inside a polled query. A missing or null answer reads as
 * off, so the button downloads — which always works.
 *
 * A document the server does not send (no printer mapped for it, RxPrint switched off since the
 * page loaded, the queue down) is downloaded instead, so a Print tap never leaves the operator
 * without the paper — except one the server reports as replaced or removed (`reason: 'gone'`):
 * the copy the page holds is out of date, so the operator is told to reload instead.
 */
import { useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { alert } from 'equasion-lib';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';

export const PRINT_SALES_ORDER_DOCUMENTS = gql`
  mutation WarehousePrintSalesOrderDocuments(
    $companyId: ID!
    $orderId: ID!
    $documentIds: [ID!]!
  ) {
    salesOrderPrintDocuments(
      companyId: $companyId
      orderId: $orderId
      documentIds: $documentIds
    ) {
      orderId
      queued
      documents {
        documentId
        descriptor
        status
        reason
        message
      }
    }
  }
`;

export const COMPANY_RXPRINT_ENABLED = gql`
  query WarehouseCompanyRxPrintEnabled($companyId: ID!) {
    company(id: $companyId) {
      id
      printNodeEnabled
    }
  }
`;

/**
 * A send that has not answered by now gives the buttons back (the HTTP link has no timeout) and
 * warns that the documents may still print, so the operator checks the printer before tapping
 * again. A late answer is still handled — its toast and any downloads — when it arrives.
 */
export const DOCUMENT_PRINT_DEADLINE_MS = 20 * 1000;

export const rxPrintEnabledFor = (
  company: { printNodeEnabled?: boolean | null } | null | undefined
): boolean => company?.printNodeEnabled === true;

/**
 * Whether the vendor has RxPrint on, read once when the screen opens (from the cache straight away,
 * then refreshed) — for screens whose own document query polls, so the flag is not re-read per tick.
 */
export function useRxPrintEnabled(companyId: string | null | undefined): boolean {
  const { data } = useQuery(COMPANY_RXPRINT_ENABLED, {
    variables: { companyId },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });
  return rxPrintEnabledFor(data?.company);
}

/** "Re-print Invoice" with RxPrint on, "Download Invoice" without. */
export const documentActionLabel = (rxPrintEnabled: boolean, name: string): string =>
  `${rxPrintEnabled ? 'Re-print' : 'Download'} ${name}`;

/**
 * Why a send failed, in words for the operator. An API without `salesOrderPrintDocuments` (an
 * older server than this app) rejects the request as invalid with an HTTP 400, which Apollo reports
 * as a network error carrying the GraphQL errors — so it is looked for there as well as in
 * `graphQLErrors`, rather than collapsing into the generic fallback.
 */
export function documentPrintFailureMessage(error: any): string {
  const errors = [
    ...(error?.graphQLErrors ?? []),
    ...(error?.networkError?.result?.errors ?? [])
  ];
  const unknownCall = errors.some(
    (e: any) =>
      e?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' ||
      /salesOrderPrintDocuments|SalesOrderDocumentsPrint/.test(String(e?.message ?? ''))
  );
  if (unknownCall) return 'This server does not support RxPrint re-prints yet.';
  const operatorMessage = operatorFacingMessage(error, '');
  if (operatorMessage) return operatorMessage;
  if (error?.networkError && !errors.length) return 'Could not reach the server.';
  return 'Could not send the document to the printer.';
}

export type DocumentPrintResult = {
  orderId: string;
  queued: number;
  documents: {
    documentId: string;
    descriptor?: string | null;
    status: string;
    reason?: string | null;
    message?: string | null;
  }[];
};

/** `alert(title, message, seconds, type)` arguments. */
export type DocumentPrintNotice = {
  title: string;
  message: string;
  type: 'success' | 'warning' | 'error';
};

/** One document for `send`: its id, and how to download it when it is not sent. */
export type PrintableOrderDocument = {
  id?: string | null;
  download?: () => void;
};

/** Not sent, and safe to download in its place: anything but a copy the server says is out of date. */
export const downloadInstead = (document: DocumentPrintResult['documents'][number]): boolean =>
  document.status !== 'queued' && document.reason !== 'gone';

/** The toast for a print result. `downloaded` = how many of the documents not sent were downloaded. */
export function documentPrintNotice(
  result: DocumentPrintResult,
  downloaded = 0
): DocumentPrintNotice {
  const total = result.documents.length;
  const notSent = total - result.queued;
  const problems = [
    ...new Set(
      result.documents
        .filter((d) => d.status !== 'queued')
        .map((d) => d.message || 'Not sent.')
    )
  ];
  const tail = !downloaded
    ? []
    : downloaded === notSent
    ? [notSent === 1 ? 'Downloaded it instead.' : 'Downloaded them instead.']
    : [`Downloaded ${downloaded} of them instead.`];
  if (result.queued === total) {
    return {
      title: 'Sent to printer',
      message:
        total === 1
          ? 'The document is printing through RxPrint.'
          : `${total} documents are printing through RxPrint.`,
      type: 'success'
    };
  }
  if (result.queued === 0) {
    return {
      title: 'Not printed',
      message: [...problems, ...tail].join(' '),
      type: downloaded ? 'warning' : 'error'
    };
  }
  return {
    title: 'Sent to printer',
    message: [`${result.queued} of ${total} documents are printing.`, ...problems, ...tail].join(
      ' '
    ),
    type: 'warning'
  };
}

/**
 * Send documents to their RxPrint printers. `send(key, …)` marks `sendingKey` with the tapped
 * button's key until the server answers, and ignores every tap while one is in flight, so a double
 * tap prints once.
 */
export function useSendDocumentsToPrinter(companyId: string | null | undefined) {
  const [mutate] = useMutation(PRINT_SALES_ORDER_DOCUMENTS);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  // Each send's number: only the newest send may release the buttons.
  const attemptRef = useRef(0);
  const busyRef = useRef(false);

  const send = async (
    key: string,
    orderId: string | null | undefined,
    documents: PrintableOrderDocument[]
  ): Promise<void> => {
    const docs = documents.filter((d): d is PrintableOrderDocument & { id: string } => !!d?.id);
    if (busyRef.current || !companyId || !orderId || !docs.length) return;
    const attempt = ++attemptRef.current;
    const newest = () => attemptRef.current === attempt;
    const release = () => {
      busyRef.current = false;
      setSendingKey(null);
    };
    busyRef.current = true;
    setSendingKey(key);
    const deadline = setTimeout(() => {
      if (newest()) release();
      // No download here: the request may yet succeed, and the paper with it.
      alert(
        'Still waiting on RxPrint',
        'The documents may still print. Check the printer before you try again.',
        6,
        'warning'
      );
    }, DOCUMENT_PRINT_DEADLINE_MS);
    try {
      const { data } = await mutate({
        variables: { companyId, orderId, documentIds: docs.map((d) => d.id) }
      });
      const result: DocumentPrintResult | undefined = data?.salesOrderPrintDocuments;
      if (!result) throw new Error('Print answered without a result');
      const downloadIds = new Set(
        result.documents.filter(downloadInstead).map((d) => String(d.documentId))
      );
      const fallbacks = docs.filter((d) => downloadIds.has(d.id) && d.download);
      fallbacks.forEach((d) => d.download!());
      const notice = documentPrintNotice(result, fallbacks.length);
      alert(notice.title, notice.message, 6, notice.type);
    } catch (e) {
      // Refused outright (RxPrint switched off since the page loaded, permission): download all.
      const fallbacks = docs.filter((d) => d.download);
      fallbacks.forEach((d) => d.download!());
      alert(
        'Could not print',
        [
          documentPrintFailureMessage(e),
          fallbacks.length ? 'Downloaded instead.' : 'Try again.'
        ].join(' '),
        6,
        fallbacks.length ? 'warning' : 'error'
      );
    } finally {
      clearTimeout(deadline);
      if (newest()) release();
    }
  };

  return { send, sendingKey };
}
